import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { map, tap, catchError } from 'rxjs/operators';
import { empty, throwError, of } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ITenantsApi, Tenant, User, IUsersResult, Subscription, UpgradeInquiry } from 'app/tenants/tenant.model';
// import Axios from  'axios-observable';
// import Axios from '@app/blocks/utils/axios-observable';
// import Axios from '../utils/axios-observable';
import { AjaxResponse, ajax } from 'rxjs/ajax';
import { AuthService } from '../security/auth-service';
import { handleError } from '../utils/exception.helper';
import {
    AdminAiCreditPurchaseRequest,
    AdminSubscriptionOverview,
    BillingCheckoutResult,
    BillingQuote,
    BillingQuoteRequest
} from 'app/tenants/view-tenant/subscription/admin-subscription-overview.model';


@Injectable()
export class SubscriptionsService {

    private baseUrl: string;
    private tenantsBaseUrl: string;
    /**
    * Constructor
    * @param {HttpClient} _httpClient
    */
    constructor(
        private httpClient: HttpClient,
        private _authService: AuthService
    ) {
        this.baseUrl = environment.backEnd;
        this.tenantsBaseUrl = this.baseUrl + '/api/subscriptionAdmin';
    }

    getSubscriptionById(id: string): Observable<Subscription> {
        return this.httpClient
            .get<Subscription>(`${this.tenantsBaseUrl}/getSubscriptionById/${id}`);
    }

    getAdminBillingOverview(
        tenantId: string,
        paymentPage: number,
        historyPage: number,
        pageSize: number): Observable<AdminSubscriptionOverview> {
        return this.httpClient.get<AdminSubscriptionOverview>(
            `${this.tenantsBaseUrl}/${tenantId}/billingOverview?paymentPage=${paymentPage}&historyPage=${historyPage}&pageSize=${pageSize}`);
    }

    createAdminPlanChange(tenantId: string, planId: string, billingPeriod: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/planChange`,
            { planId, billingPeriod });
    }

    // Loads the tenant discount used for subscription billing.
    getSubscriptionDiscount(tenantId: string): Observable<any> {
        return this.httpClient.get(
            `${this.tenantsBaseUrl}/${tenantId}/subscriptionDefaultDiscount`);
    }

    // Saves the tenant discount used for current and future subscription billing.
    updateSubscriptionDiscount(
        tenantId: string,
        enabled: boolean,
        percent: number,
        note: string): Observable<any> {
        return this.httpClient.put(
            `${this.tenantsBaseUrl}/${tenantId}/subscriptionDefaultDiscount`,
            { enabled, percent, note });
    }

    approveManualPayment(tenantId: string, transactionId: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/payments/${transactionId}/approve`,
            null);
    }

    // Cancels one pending manual payment selected from payment history.
    cancelManualPayment(tenantId: string, transactionId: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/payments/${transactionId}/cancel`,
            null);
    }

    // Loads the calculated cost for an administrator add-on change.
    getAdminAddOnQuote(
        tenantId: string,
        request: BillingQuoteRequest): Observable<BillingQuote> {
        const query = this.buildAddOnQuery(request);
        return this.httpClient.get<BillingQuote>(
            `${this.tenantsBaseUrl}/${tenantId}/addOnQuote?${query}`);
    }

    // Creates a pending manual add-on payment or schedules a reduction.
    createAdminAddOnChange(
        tenantId: string,
        request: BillingQuoteRequest): Observable<BillingCheckoutResult> {
        return this.httpClient.post<BillingCheckoutResult>(
            `${this.tenantsBaseUrl}/${tenantId}/addOnChange`,
            request);
    }

    // Creates a pending AI credit payment or immediately completes a free grant.
    createAdminAiCreditPurchase(
        tenantId: string,
        request: AdminAiCreditPurchaseRequest): Observable<BillingCheckoutResult> {
        return this.httpClient.post<BillingCheckoutResult>(
            `${this.tenantsBaseUrl}/${tenantId}/aiCredits`,
            request);
    }

    // Cancels a scheduled add-on quantity change.
    cancelScheduledAddOnChange(tenantId: string, type: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/scheduledAddOnChange/${encodeURIComponent(type)}/cancel`,
            null);
    }

    cancelPendingPayment(tenantId: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/pendingPayment/cancel`,
            null);
    }

    cancelScheduledPlanChange(tenantId: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/scheduledPlanChange/cancel`,
            null);
    }

    setAutoRenew(tenantId: string, enabled: boolean): Observable<any> {
        return this.httpClient.put(
            `${this.tenantsBaseUrl}/${tenantId}/autoRenew`,
            { enabled });
    }

    manageTrial(
        tenantId: string,
        durationUnit: string,
        duration: number,
        endDate: any,
        limits: any): Observable<any> {
        return this.httpClient.put(
            `${this.tenantsBaseUrl}/${tenantId}/extendTrial`,
            { durationUnit, duration, endDate: endDate || null, limits });
    }

    // Serializes an add-on quote request for the administrator endpoint.
    private buildAddOnQuery(request: BillingQuoteRequest): string {
        const values = {
            flow: request.flow,
            planId: request.planId,
            extraUsers: request.extraUsers,
            extraStorageGb: request.extraStorageGb,
            whatsappMessages: request.whatsappMessages,
            paymentMethod: request.paymentMethod,
            billingPeriod: request.billingPeriod
        };

        return Object.keys(values)
            .filter(key => values[key] !== null && values[key] !== undefined)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(values[key])}`)
            .join('&');
    }

    disableSubscriptionAccess(tenantId: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/disableAccess`,
            null);
    }

    restoreSubscriptionAccess(tenantId: string): Observable<any> {
        return this.httpClient.post(
            `${this.tenantsBaseUrl}/${tenantId}/restoreAccess`,
            null);
    }

    getCurrentUpgradeSubscription(tenantId: string): Observable<Subscription> {
        return this.httpClient
            .get<Subscription>(`${this.tenantsBaseUrl}/${tenantId}/getCurrentUpgradeSubscription`);
    }

    getUpgradeSubscriptionInquiry(tenantId: string,subscription: Subscription): Observable<UpgradeInquiry> {
        return ajax.get(
            `${this.tenantsBaseUrl}/${tenantId}/getUpgradeSubscriptionInquiry?durationUnit=${subscription.durationUnit}&duration=${subscription.duration}&planId=${subscription.planId}`,
            {
                Authorization: `Bearer ${this._authService.getAccessToken()}`,
                'Content-Type': 'application/json'
            },
            ).pipe(
                map(response => response.response),
                catchError(handleError)
            );
        // return this.httpClient
        //     .get<Subscription>(`${this.tenantsBaseUrl}/${tenantId}/getUpgradeSubscriptionInquiry?durationUnit=month&duration=3&planId=660570837c41d74f7c2af655`);
    }

    addSubscription(tenantId: string, data: any): Observable<Subscription> {
        return ajax.post(
            `${this.tenantsBaseUrl}/${tenantId}/addSubscription`,
            data            , 
            {
                Authorization: `Bearer ${this._authService.getAccessToken()}`,
                'Content-Type': 'application/json'
            },
            ).pipe(
                map(response => response.response),
                catchError(handleError)
            );

        // return this.httpClient
        //     .post<Subscription>(`${this.tenantsBaseUrl}/${tenantId}/addSubscription`, data);
    }
    
    cancelUpgradeSubscription(tenantId: string) {

        return ajax.put(
            `${this.tenantsBaseUrl}/${tenantId}/cancelUpgradeSubscription`,
            null, 
            {
                Authorization: `Bearer ${this._authService.getAccessToken()}`,
                'Content-Type': 'application/json'
            },
            ).pipe(
                map(response => response.response),
                catchError(handleError)
            );
    }
    updateSubscription(tenantId: string, data: any) {

        return ajax.put(
            `${this.tenantsBaseUrl}/${tenantId}/updateSubscription`,
            data, 
            {
                Authorization: `Bearer ${this._authService.getAccessToken()}`,
                'Content-Type': 'application/json'
            },
            ).pipe(
                map(response => response.response),
                catchError(handleError)
            );
    }

    updateSubscriptionStatus(tenantId: string, data: any) {

        return ajax.put(
            `${this.tenantsBaseUrl}/${tenantId}/updateSubscriptionStatus`,
            data, 
            {
                Authorization: `Bearer ${this._authService.getAccessToken()}`,
                'Content-Type': 'application/json'
            },
            ).pipe(
                map(response => response.response),
                catchError(handleError)
            );
    }

    addTrial(tenantId: string, data: any) {

        return ajax.put(
            `${this.tenantsBaseUrl}/${tenantId}/addTrialSubscription`,
            data, 
            {
                Authorization: `Bearer ${this._authService.getAccessToken()}`,
                'Content-Type': 'application/json'
            },
            ).pipe(
                map(response => response.response),
                catchError(handleError)
            );
    }
}
